import { X } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * Right-hand drawer (design system fn-drawer) above the page, no backdrop so the map stays usable.
 * The header keeps icon, eyebrow and actions on one bar and gives the title the full width below,
 * so long names wrap instead of squeezing the buttons. Esc closes it unless a modal dialog is open.
 */
export function Drawer({
  eyebrow,
  title,
  subtitle,
  badge,
  icon,
  actions,
  footer,
  onClose,
  children,
  label,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Status badge, shown under the title next to the subtitle. */
  badge?: ReactNode;
  icon?: ReactNode;
  actions?: ReactNode;
  footer?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  label: string;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !document.querySelector('dialog[open]')) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <aside
      className="fn-drawer"
      role="complementary"
      aria-label={label}
      style={{ position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 60 }}
    >
      <div className="fn-drawer__header">
        <div className="fn-drawer__bar">
          {icon}
          <span className="fn-drawer__eyebrow">
            {typeof eyebrow === 'string' ? <span className="fn-truncate">{eyebrow}</span> : eyebrow}
          </span>
          <div className="fn-drawer__actions">
            {actions}
            <button
              type="button"
              className="fn-icon-btn fn-icon-btn--ghost fn-icon-btn--sm"
              aria-label="Tutup panel"
              title="Tutup (Esc)"
              onClick={onClose}
            >
              <X className="i i--sm" aria-hidden />
            </button>
          </div>
        </div>
        <div className="fn-drawer__heading">
          <h2 className="fn-drawer__title" title={typeof title === 'string' ? title : undefined}>
            {title}
          </h2>
          {(badge || subtitle) && (
            <div className="fn-drawer__meta">
              {badge}
              {subtitle && <span className="fn-drawer__subtitle mono">{subtitle}</span>}
            </div>
          )}
        </div>
      </div>
      <div className="fn-drawer__body">{children}</div>
      {footer && <div className="fn-drawer__footer">{footer}</div>}
    </aside>,
    document.body,
  );
}
